import { TOKEN_ADDRESS } from "@silo/config";
import {
  balancerV2PriceProviderAddress,
  chainlinkV3PriceProviderAddress,
  crvUSDViaUSDCPriceProviderETHAddress,
  diaPriceProviderV2Address,
  diaPriceProviderV2Y2KAddress,
  forwarderPriceProviderAddress,
  gOHMPriceProviderAddress,
  uniswapV3PriceProviderAddress,
  uniswapV3PriceProviderV2Address,
  uniswapV3PriceProviderV3Address,
  wSTETHPriceByChainlinkArbitrumAddress,
  wSTETHPriceByChainlinkOptimismAddress,
  wSTETHPriceProviderAddress,
} from "@silo/contract-addresses";
import { dia2PriceProviderV2Address } from "@silo/contract-addresses/src/generated";
import {
  Address,
  ADDRESS_ZERO,
  NATIVE_ORACLE_KEY,
  OracleKey,
  ProtocolKey,
} from "@silo/types";

const redstoneAddresses = [
  TOKEN_ADDRESS.ethereum.weETH,
  TOKEN_ADDRESS.ethereum.ETHx,
  TOKEN_ADDRESS.ethereum.sUSDe,
  TOKEN_ADDRESS.ethereum.rsETH,
  TOKEN_ADDRESS.arbitrum.weETH,
  TOKEN_ADDRESS.arbitrum.PREMIA,
  TOKEN_ADDRESS.arbitrum.ETHplus,
  TOKEN_ADDRESS.base.bsdETH,
];

const api3Addresses = [TOKEN_ADDRESS.arbitrum.RDNT];

interface DetermineOracleKeyParams {
  protocolKey: ProtocolKey;
  tokenAddress: Address;
  isNative: boolean;
  priceProvider: Address;
}

export function determineOracleKey({
  protocolKey,
  tokenAddress,
  isNative,
  priceProvider,
}: DetermineOracleKeyParams): OracleKey | null {
  if (redstoneAddresses.includes(tokenAddress)) {
    return "redstone";
  }

  if (api3Addresses.includes(tokenAddress)) {
    return "api3";
  }

  if (tokenAddress === TOKEN_ADDRESS.arbitrum.stEUR) {
    return "stEUR-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.arbitrum.PTeETH) {
    return "PT-eETH-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.ethereum.PTezETH) {
    return "PT-ezETH-renzo-oracle";
  }

  if (
    [
      TOKEN_ADDRESS.arbitrum.PTezETH,
      TOKEN_ADDRESS.ethereum.PTezETH_DEC2024,
      TOKEN_ADDRESS.arbitrum.PTezETH_SEPT2024,
    ].includes(tokenAddress)
  ) {
    return "PT-ezETH-chainlink-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.ethereum.PTrswETH) {
    return "PT-rswETH-oracle";
  }

  if (
    [
      TOKEN_ADDRESS.ethereum.PTweETH,
      TOKEN_ADDRESS.ethereum.PTweETH_DEC2024,
      TOKEN_ADDRESS.ethereum.PT_weETH_26SEP2024,
      TOKEN_ADDRESS.arbitrum.PTweETH,
      TOKEN_ADDRESS.arbitrum.PTweETH_SEPT2024,
      TOKEN_ADDRESS.arbitrum.PTweETH_DEC2024,
      TOKEN_ADDRESS.arbitrum.PTweETH_JUN2025,
    ].includes(tokenAddress)
  ) {
    return "PT-weETH-oracle";
  }

  if (
    [
      TOKEN_ADDRESS.ethereum.PTrsETH,
      TOKEN_ADDRESS.ethereum.PTrsETH_DEC2024,
      TOKEN_ADDRESS.arbitrum.PTrsETH,
      TOKEN_ADDRESS.arbitrum.PTrsETH_SEPT2024,
      TOKEN_ADDRESS.arbitrum.PTrsETH_DEC2024,
      TOKEN_ADDRESS.arbitrum.PTrsETH_JUN2025,
    ].includes(tokenAddress)
  ) {
    return "PT-rsETH-oracle";
  }

  if (
    tokenAddress === TOKEN_ADDRESS.arbitrum.PTUSDe ||
    tokenAddress === TOKEN_ADDRESS.arbitrum.ptUSDE_NOV2024
  ) {
    return "PT-USDe-oracle";
  }

  if (
    tokenAddress === TOKEN_ADDRESS.ethereum.PT_pufETH_26SEP2024 ||
    tokenAddress === TOKEN_ADDRESS.ethereum.PTpufETH_DEC2024
  ){
    return "PT-pufETH-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.base.PT_LBTC_MAY_2025) {
    return "PT-LBTC-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.arbitrum.WOETH) {
    return "woETH-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.arbitrum.wUSDplus || tokenAddress === TOKEN_ADDRESS.base.wUSDPlus) {
    return "wUSD+-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.optimism.uniBTC) {
    return "uniBTC-oracle";
  }

  if (
      tokenAddress === TOKEN_ADDRESS.optimism.wUSDM ||
      tokenAddress === TOKEN_ADDRESS.arbitrum.wUSDM ||
      tokenAddress === TOKEN_ADDRESS.base.wUSDM ||
      tokenAddress === TOKEN_ADDRESS.base.hyUSD
  ) {
    return "chronicle";
  }

  if (tokenAddress === TOKEN_ADDRESS.ethereum.pzETH) {
    return "pzETH-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.ethereum.amphrETH) {
    return "amphrETH-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.ethereum.rstETH) {
    return "rstETH-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.ethereum.re7LRT) {
    return "re7LRT-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.arbitrum.dlcBTC) {
    return "dlcBTC-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.arbitrum.dlcBTC) {
    return "dlcBTC-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.base.sUSDz){
    return "sUSDz-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.base.wSuperOETHb){
    return "wSuperOETHb-oracle";
  }

  if (tokenAddress === TOKEN_ADDRESS.base.pumpBTC){
    return "pumpBTC-oracle";
  }

  if (isNative) {
    return NATIVE_ORACLE_KEY;
  }

  switch (priceProvider) {
    case ADDRESS_ZERO:
      return null;
    case uniswapV3PriceProviderAddress[protocolKey]:
      return "uniswap-v3";
    case uniswapV3PriceProviderV2Address[protocolKey]:
      return "uniswap-v3-v2";
    case uniswapV3PriceProviderV3Address[protocolKey]:
      return "uniswap-v3-v3";
    case balancerV2PriceProviderAddress[protocolKey]:
      return "balancer";
    case chainlinkV3PriceProviderAddress[protocolKey]:
      return "chainlink";
    case wSTETHPriceProviderAddress[protocolKey]:
    case wSTETHPriceByChainlinkArbitrumAddress[protocolKey]:
    case wSTETHPriceByChainlinkOptimismAddress[protocolKey]:
      return "wstETH-oracle";
    case gOHMPriceProviderAddress[protocolKey]:
      return "gOHM-oracle";
    case dia2PriceProviderV2Address[protocolKey]:
    case diaPriceProviderV2Address[protocolKey]:
    case diaPriceProviderV2Y2KAddress[protocolKey]:
      return "dia";
    case forwarderPriceProviderAddress[protocolKey]:
      return "curve-lp";
    case crvUSDViaUSDCPriceProviderETHAddress[protocolKey]:
      return "crvUSD-oracle";
  }

  return null;
}
